/**
 * features/activities/hooks — hooks de leitura e mutação de atividades.
 *
 * `useActivities` mantém a interface original para compatibilidade retroativa
 * com quaisquer consumidores que ainda usem o hook diretamente.
 * A ActivitiesPage consome `useTenantQuery` diretamente (padrão Wave 5).
 *
 * `useActivityMutations` expõe criar, atualizar, concluir, reagendar e excluir
 * atividades via TanStack Query mutations, com invalidação de cache automática.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTenantQuery } from "../../app/providers/QueryProvider.js";
import type { ActivityItem, MyDayResponse, TaskSettings, ReviewStaleInput } from "./types.js";
import type { CreateActivityInput, UpdateActivityInput } from "./types.js";
import type { ListActivitiesParams } from "../../data/repos/activitiesRepo.js";
import {
  listActivities,
  createActivity,
  updateActivity,
  completeActivity,
  rescheduleActivity,
  deleteActivity,
  getMyDay,
  prioritizeActivity,
  reviewStaleActivity,
  reassignActivity,
  reopenActivity,
  pinActivity,
  getTaskSettings,
  updateTaskSettings,
} from "../../data/repos/activitiesRepo.js";

// ---------------------------------------------------------------------------
// useActivities — compatibilidade retroativa
// ---------------------------------------------------------------------------

export interface UseActivitiesResult {
  activities: ActivityItem[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Parâmetros opcionais para filtrar a listagem de atividades.
 *
 * Quando fornecidos, fazem parte da queryKey, garantindo que consumidores com
 * filtros distintos não compartilhem cache (PLAUSIBLE — finding activities/hooks.ts:37).
 */
export interface UseActivitiesParams {
  /** Filtra por dono da atividade. */
  readonly ownerId?: string;
  /** Filtra por BU. */
  readonly buId?: string;
  /** Filtra por estado (pending/completed). */
  readonly stateFilter?: string | null;
  /** Página de resultados (paginação). */
  readonly page?: number;
}

export function useActivities(params: UseActivitiesParams = {}): UseActivitiesResult {
  // A queryKey inclui os parâmetros efetivos para evitar colisão de cache entre
  // consumidores com filtros distintos.
  const queryParams = Object.keys(params).length > 0 ? params : null;
  const query = useTenantQuery(
    "activities:list",
    () => listActivities(),
    { params: queryParams, staleTime: 30_000 },
  );

  return {
    activities: query.data ? [...query.data.items] : [],
    loading: query.isLoading,
    error: query.isError ? "WEB-ERR-009" : null,
    refetch: () => { void query.refetch(); },
  };
}

// ---------------------------------------------------------------------------
// useActivityMutations — criar, atualizar, concluir, reagendar, excluir
// ---------------------------------------------------------------------------

export interface ActivityMutationError {
  readonly code: string;
  readonly message: string;
}

/**
 * Hook centralizado para todas as operações de escrita de atividades.
 *
 * Após cada mutação bem-sucedida, invalida a query key `["activities"]`
 * (prefixo amplo que cobre todas as listas paginadas da área).
 *
 * Uso:
 * ```tsx
 * const { create, update, complete, reschedule, remove, isLoading, error } = useActivityMutations();
 * await create({ type: "call", title: "...", ... });
 * ```
 */
// ---------------------------------------------------------------------------
// useMyDay — "Meu Dia" seccionado (TASK-30)
// ---------------------------------------------------------------------------

export interface UseMyDayResult {
  data: MyDayResponse | undefined;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook de "Meu Dia" turbinado (REQ-14).
 *
 * Consome GET /api/v1/activities/me/day e retorna as 4 seções:
 *   - staleReview: encalhadas pendentes de revisão (exibidas no topo)
 *   - migrated: migradas automaticamente (badge de contador)
 *   - today: do dia atual
 *   - appointments: compromissos do dia (com hora)
 *
 * Ordenação Q1→Q4 vinda do servidor — frontend NÃO re-ordena (REQ-11).
 */
export function useMyDay(): UseMyDayResult {
  const query = useTenantQuery<MyDayResponse>(
    "activities:my-day",
    () => getMyDay(),
    { staleTime: 30_000 },
  );
  return {
    data: query.data,
    loading: query.isLoading,
    error: query.isError ? "WEB-ERR-009" : null,
    refetch: () => { void query.refetch(); },
  };
}

// ---------------------------------------------------------------------------
// useTaskSettings — configurações do tenant (TASK-33)
// ---------------------------------------------------------------------------

export interface UseTaskSettingsResult {
  settings: TaskSettings | undefined;
  loading: boolean;
  error: string | null;
  save: (s: TaskSettings) => Promise<void>;
  isSaving: boolean;
  saveError: string | null;
}

/**
 * Hook de configuração de tarefas do tenant (REQ-19).
 * Visível/editável apenas para Tenant Admin (gate na UI).
 */
export function useTaskSettings(): UseTaskSettingsResult {
  const qc = useQueryClient();
  const query = useTenantQuery<TaskSettings>(
    "activities:task-settings",
    () => getTaskSettings(),
    { staleTime: 60_000 },
  );

  const saveMutation = useMutation<void, Error, TaskSettings>({
    mutationFn: (s) => updateTaskSettings(s),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["activities:task-settings"] });
    },
  });

  return {
    settings: query.data,
    loading: query.isLoading,
    error: query.isError ? "WEB-ERR-009" : null,
    save: (s) => saveMutation.mutateAsync(s),
    isSaving: saveMutation.isPending,
    saveError: saveMutation.isError ? "WEB-ERR-013" : null,
  };
}

// ---------------------------------------------------------------------------
// useActivityMutations — estendido com novos endpoints (TASK-30/31/32)
// ---------------------------------------------------------------------------

export function useActivityMutations() {
  const qc = useQueryClient();

  /** Invalida todas as queries de atividades (prefix-match). */
  const invalidateActivities = () => void qc.invalidateQueries({ queryKey: ["activities"] });

  // POST /v1/activities
  const createMutation = useMutation<{ id: string }, Error, CreateActivityInput>({
    mutationFn: (input) => createActivity(input),
    onSuccess: invalidateActivities,
  });

  // PUT /v1/activities/{id}
  const updateMutation = useMutation<void, Error, { id: string; input: UpdateActivityInput }>({
    mutationFn: ({ id, input }) => updateActivity(id, input),
    onSuccess: invalidateActivities,
  });

  // PATCH /v1/activities/{id}/complete
  const completeMutation = useMutation<void, Error, string>({
    mutationFn: (id) => completeActivity(id).then(() => undefined),
    onSuccess: invalidateActivities,
  });

  // PATCH /v1/activities/{id}/reschedule
  const rescheduleMutation = useMutation<void, Error, { id: string; dueAt: string }>({
    mutationFn: ({ id, dueAt }) => rescheduleActivity(id, dueAt),
    onSuccess: invalidateActivities,
  });

  // DELETE /v1/activities/{id}
  const removeMutation = useMutation<void, Error, string>({
    mutationFn: (id) => deleteActivity(id),
    onSuccess: invalidateActivities,
  });

  // PATCH /v1/activities/{id}/prioritize
  const prioritizeMutation = useMutation<
    { activityId: string; quadrant: string; priority: string },
    Error,
    { id: string; isUrgent: boolean; isImportant: boolean }
  >({
    mutationFn: ({ id, isUrgent, isImportant }) => prioritizeActivity(id, isUrgent, isImportant),
    onSuccess: invalidateActivities,
  });

  // POST /v1/activities/{id}/review
  const reviewStaleMutation = useMutation<void, Error, { id: string; input: ReviewStaleInput }>({
    mutationFn: ({ id, input }) => reviewStaleActivity(id, input),
    onSuccess: invalidateActivities,
  });

  // PATCH /v1/activities/{id}/reassign
  const reassignMutation = useMutation<void, Error, { id: string; assigneeId: string }>({
    mutationFn: ({ id, assigneeId }) => reassignActivity(id, assigneeId),
    onSuccess: invalidateActivities,
  });

  // PATCH /v1/activities/{id}/reopen
  const reopenMutation = useMutation<void, Error, string>({
    mutationFn: (id) => reopenActivity(id),
    onSuccess: invalidateActivities,
  });

  // PATCH /v1/activities/{id}/pin
  const pinMutation = useMutation<void, Error, { id: string; pinnedDate: string | null }>({
    mutationFn: ({ id, pinnedDate }) => pinActivity(id, pinnedDate),
    onSuccess: invalidateActivities,
  });

  const isLoading =
    createMutation.isPending ||
    updateMutation.isPending ||
    completeMutation.isPending ||
    rescheduleMutation.isPending ||
    removeMutation.isPending ||
    prioritizeMutation.isPending ||
    reviewStaleMutation.isPending ||
    reassignMutation.isPending ||
    reopenMutation.isPending ||
    pinMutation.isPending;

  const error =
    createMutation.isError ||
    updateMutation.isError ||
    completeMutation.isError ||
    rescheduleMutation.isError ||
    removeMutation.isError ||
    prioritizeMutation.isError ||
    reviewStaleMutation.isError ||
    reassignMutation.isError ||
    reopenMutation.isError ||
    pinMutation.isError
      ? "WEB-ERR-013"
      : null;

  return {
    create: (input: CreateActivityInput) => createMutation.mutateAsync(input),
    update: (id: string, input: UpdateActivityInput) => updateMutation.mutateAsync({ id, input }),
    complete: (id: string) => completeMutation.mutateAsync(id),
    reschedule: (id: string, dueAt: string) => rescheduleMutation.mutateAsync({ id, dueAt }),
    remove: (id: string) => removeMutation.mutateAsync(id),
    prioritize: (id: string, isUrgent: boolean, isImportant: boolean) =>
      prioritizeMutation.mutateAsync({ id, isUrgent, isImportant }),
    reviewStale: (id: string, input: ReviewStaleInput) =>
      reviewStaleMutation.mutateAsync({ id, input }),
    reassign: (id: string, assigneeId: string) => reassignMutation.mutateAsync({ id, assigneeId }),
    reopen: (id: string) => reopenMutation.mutateAsync(id),
    pin: (id: string, pinnedDate: string | null) => pinMutation.mutateAsync({ id, pinnedDate }),
    isLoading,
    error,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isCompleting: completeMutation.isPending,
    isRescheduling: rescheduleMutation.isPending,
    isRemoving: removeMutation.isPending,
    isPrioritizing: prioritizeMutation.isPending,
    isReviewing: reviewStaleMutation.isPending,
    isReassigning: reassignMutation.isPending,
    isReopening: reopenMutation.isPending,
    isPinning: pinMutation.isPending,
  };
}

// ---------------------------------------------------------------------------
// useNextActivity — "próxima atividade" de uma oportunidade ou parceria (Fase 5)
// ---------------------------------------------------------------------------

export interface UseNextActivityParams {
  /** Busca a próxima atividade vinculada a esta oportunidade. */
  readonly opportunityId?: string | null;
  /** Busca a próxima atividade vinculada a este parceiro. Mutuamente exclusivo com opportunityId. */
  readonly partnerId?: string | null;
}

export interface UseNextActivityResult {
  /** Atividade não-terminal com o menor dueDate/scheduledAt, ou null quando não há. */
  readonly nextActivity: ActivityItem | null;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly refetch: () => void;
}

/**
 * Escolhe, dentre as atividades informadas, a de menor `dueDate` cujo `state`
 * ainda não é terminal ("done"). Exportada para teste unitário isolado.
 */
export function pickNextActivity(items: readonly ActivityItem[]): ActivityItem | null {
  const open = items.filter((a) => a.state !== "done");
  if (open.length === 0) return null;
  return [...open].sort((a, b) => a.dueDate.localeCompare(b.dueDate))[0] ?? null;
}

/**
 * useNextActivity — busca a atividade futura mais próxima vinculada a uma
 * oportunidade ou parceria (Req Fase 5).
 *
 * A escolha do vínculo é feita pelo chamador: informe `opportunityId` OU
 * `partnerId` (nunca ambos). Quando nenhum é informado, a query permanece
 * desabilitada e `nextActivity` é null.
 */
export function useNextActivity(params: UseNextActivityParams): UseNextActivityResult {
  const { opportunityId, partnerId } = params;
  const enabled = !!opportunityId || !!partnerId;

  const listParams: ListActivitiesParams = opportunityId
    ? { opportunityId }
    : partnerId
      ? { partnerId }
      : {};

  const query = useTenantQuery(
    "next-activity",
    () => listActivities(listParams),
    {
      params: { opportunityId: opportunityId ?? null, partnerId: partnerId ?? null },
      enabled,
    },
  );

  return {
    nextActivity: query.data ? pickNextActivity(query.data.items) : null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => { void query.refetch(); },
  };
}
